import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import DataTable from '../../components/ui/DataTable';
import CreateTenantModal from '../../components/modals/CreateTenantModal';
import TenantPermissionsModal from '../../components/modals/TenantPermissionsModal';
import MasterFeaturesModal from '../../components/modals/MasterFeaturesModal';
import { registrationService } from '../../api/registrationService';
import toast from 'react-hot-toast';
import { 
  Building2, 
  Plus, 
  Search,
  Edit,
  Settings,
  Sparkles,
} from 'lucide-react';

const CreateTenant = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [permissionsDrawerOpen, setPermissionsDrawerOpen] = useState(false);
  const [selectedTenantForPermissions, setSelectedTenantForPermissions] = useState(null);
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [selectedTenantForFeatures, setSelectedTenantForFeatures] = useState(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const response = await registrationService.getTenants({
        page: 0,
        size: 1000
      });

      // Handle response structure
      let tenantsData = [];
      const responseData = response?.data;
      
      if (responseData?.data?.content && Array.isArray(responseData.data.content)) {
        // PaginatedResponse format
        tenantsData = responseData.data.content;
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        tenantsData = responseData.data;
      } else if (responseData?.content && Array.isArray(responseData.content)) {
        tenantsData = responseData.content;
      } else if (Array.isArray(responseData)) {
        tenantsData = responseData;
      } else if (Array.isArray(response)) {
        tenantsData = response;
      }

      setTenants(tenantsData);
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (tenant) => {
    //console.log('Tenant created successfully:', tenant);
    // Refresh the tenant list
    loadTenants();
    setIsModalOpen(false);
    setEditingTenant(null);
  };

  const handleEdit = (tenant) => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID not found');
      return;
    }
    setEditingTenant(tenant);
    setIsModalOpen(true);
  };

  const handleManagePermissions = (tenant) => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID not found');
      return;
    }
    setSelectedTenantForPermissions(tenant);
    setPermissionsDrawerOpen(true);
  };

  const handleManageFeatures = (tenant) => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID not found');
      return;
    }
    setSelectedTenantForFeatures(tenant);
    setFeaturesModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active') {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Active</Badge>;
    } else if (statusLower === 'suspended') {
      return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">Suspended</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">Inactive</Badge>;
    }
  };

  const getTierBadge = (tier) => {
    const tierLower = (tier || '').toLowerCase();
    if (tierLower === 'enterprise') {
      return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">Enterprise</Badge>;
    } else if (tierLower === 'premium') {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Premium</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">Standard</Badge>;
    }
  };

  // Filter tenants based on search term
  const filteredTenants = tenants.filter(tenant => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (tenant.tenantName || '').toLowerCase().includes(searchLower) ||
      (tenant.tenantId?.toString() || '').toLowerCase().includes(searchLower) ||
      (tenant.domain || '').toLowerCase().includes(searchLower)
    );
  });

  // Add serial numbers to tenants for display
  const tenantsWithSerial = filteredTenants.map((tenant, index) => ({
    ...tenant,
    serialNumber: index + 1
  }));

  const columns = [
    {
      key: 'serialNumber',
      label: 'S.No',
      render: (value) => <span className="text-gray-600">{value}</span>
    },
    {
      key: 'tenantName',
      label: 'Tenant Name',
      render: (value) => <span className="font-medium">{value || 'N/A'}</span>
    },
    {
      key: 'tenantId',
      label: 'Tenant ID',
      render: (value) => <span className="font-mono text-sm">{value || 'N/A'}</span>
    },
    {
      key: 'domain',
      label: 'Domain',
      render: (value) => <span>{value || '-'}</span>
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => getStatusBadge(value)
    },
    {
      key: 'subscriptionTier',
      label: 'Subscription Tier',
      render: (value, row) => getTierBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            className="h-8 w-8 p-0"
            title="Edit"
          >
            <Edit className="h-4 w-4 text-green-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleManagePermissions(row);
            }}
            className="h-8 w-8 p-0"
            title="Manage Menu Permissions"
          >
            <Settings className="h-4 w-4 text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleManageFeatures(row);
            }}
            className="h-8 w-8 p-0"
            title="Manage Master Features"
          >
            <Sparkles className="h-4 w-4 text-purple-600" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Organizational Management</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage all tenants in the system
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            setEditingTenant(null);
            setIsModalOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Tenant
        </Button>
      </div>

      {/* Search Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search tenants by name, ID, or domain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tenants ({filteredTenants.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading tenants...</span>
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No tenants found</p>
              <p className="text-gray-500 text-sm mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first tenant'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => {
                    setEditingTenant(null);
                    setIsModalOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Tenant
                </Button>
              )}
            </div>
          ) : (
            <DataTable
              data={tenantsWithSerial}
              columns={columns}
              pagination={true}
              searchable={false}
              sortable={true}
              itemsPerPage={10}
              showItemsPerPageSelector={true}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Tenant Modal */}
      <CreateTenantModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTenant(null);
        }}
        onSuccess={handleCreateSuccess}
        tenant={editingTenant}
      />

      {/* Tenant Permissions Modal */}
      <TenantPermissionsModal
        isOpen={permissionsDrawerOpen}
        onClose={() => {
          setPermissionsDrawerOpen(false);
          setSelectedTenantForPermissions(null);
        }}
        tenant={selectedTenantForPermissions}
      />

      {/* Master Features Modal */}
      <MasterFeaturesModal
        isOpen={featuresModalOpen}
        onClose={() => {
          setFeaturesModalOpen(false);
          setSelectedTenantForFeatures(null);
        }}
        tenant={selectedTenantForFeatures}
      />
    </div>
  );
};

export default CreateTenant;
